import AbrirConversaDoRobo from './pages/chat/robo/AbrirConversaDoRobo'
import { useEffect, Component } from 'react'
import type { ReactNode, ErrorInfo } from 'react'
import { BrowserRouter, Routes, Route, Navigate } from 'react-router-dom'
import { useZtnaSessionHeartbeat } from '@/hooks/useZtnaSessionHeartbeat'
import { useInactivityLogout } from '@/hooks/useInactivityLogout'
import { usePwaPersistentSession } from '@/hooks/usePwaPersistentSession'
import { InactivityWarningModal } from '@/components/InactivityWarningModal'
import { AppLayout } from '@/components/layout/AppLayout'
import { ChangePassword } from '@/pages/auth/ChangePassword'
import { ForgotPassword } from '@/pages/auth/ForgotPassword'
import { ResetPassword } from '@/pages/auth/ResetPassword'
import { SetupWizard } from '@/pages/auth/SetupWizard'
import { ForgotTotp } from '@/pages/auth/ForgotTotp'
import { SessionExpired } from '@/pages/auth/SessionExpired'
import { OnLeave } from '@/pages/auth/OnLeave'
import { SetupPhone } from '@/pages/auth/SetupPhone'
import { HomeLanding } from '@/pages/HomeLanding'
import { DevDashboard } from '@/pages/dashboard/DevDashboard'
import { PODashboard } from '@/pages/dashboard/PODashboard'
import { ManagerDashboard } from '@/pages/dashboard/ManagerDashboard'
import { Recursos } from '@/pages/recursos/Recursos'
import { Ssm } from '@/pages/ssm/Ssm'
import { Chamados } from '@/pages/chamados/Chamados'
import { NovoChamado } from '@/pages/chamados/NovoChamado'
import { ChamadoDetalhe } from '@/pages/chamados/ChamadoDetalhe'
import { ChamadosInbox } from '@/pages/chamados/ChamadosInbox'
import { Auditoria } from '@/pages/auditoria/Auditoria'
import { AuditoriaDashboard } from '@/pages/auditoria/AuditoriaDashboard'
import { AuditoriaGlobal } from '@/pages/auditoria/AuditoriaGlobal'
import { AuditoriaPoliticas } from '@/pages/auditoria/AuditoriaPoliticas'
import { FinopsHome } from '@/pages/finops/FinopsHome'
import { Recomendacoes } from '@/pages/finops/Recomendacoes'
import { AlertasV2 } from '@/pages/finops/AlertasV2'
import { RelatoriosV2 } from '@/pages/finops/RelatoriosV2'
import { RelatorioDetalheV2 } from '@/pages/finops/RelatorioDetalheV2'
import { RelatorioOrganizacao } from '@/pages/finops/RelatorioOrganizacao'
import { RelatorioAssinaturas } from '@/pages/finops/RelatorioAssinaturas'
import { ContaDetalhe } from '@/pages/finops/ContaDetalhe'
import { Usuarios } from '@/pages/admin/Usuarios'
import { LdapAdmin } from '@/pages/admin/ldap'
import { Permissoes } from '@/pages/admin/Permissoes'
import { ConexoesSso } from '@/pages/admin/sso/ConexoesSso'
import { Tenants } from '@/pages/admin/Tenants'
import { NewTenantWizard } from '@/components/admin/tenants/NewTenantWizard'
import { ProvisioningJobsList } from '@/pages/admin/ProvisioningJobsList'
import { ProvisioningJobDetail } from '@/components/admin/tenants/jobs/ProvisioningJobDetail'
// AwsOuTreeView removida em v1.24.3 — funcionalidade migrada pra Tenants page (TenantsGroupedView)
import { Roles } from '@/pages/admin/Roles'
import { Categorias } from '@/pages/admin/chamados/Categorias'
import { AlertaRules } from '@/pages/admin/chamados/AlertaRules'
import { FormTemplates } from '@/pages/admin/chamados/FormTemplates'
import { Relatorios } from '@/pages/chamados/Relatorios'
import { ComoFunciona } from '@/pages/chamados/como-funciona/ComoFunciona'
import { RelatorioMudancas } from '@/pages/chamados/RelatorioMudancas'
import { Gmud } from '@/pages/gmud/Gmud'
import { GmudForm } from '@/pages/gmud/GmudForm'
import { GmudDetalhe } from '@/pages/gmud/GmudDetalhe'
import { GmudFreezeAdmin } from '@/pages/admin/gmud/GmudFreezeAdmin'
import { ComitesAdmin } from '@/pages/admin/ccm/ComitesAdmin'
import { CertificacoesAdmin } from '@/pages/admin/certificacoes/CertificacoesAdmin'
import { Organizacoes } from '@/pages/admin/Organizacoes'
import { AwsRoleCoverage } from '@/pages/admin/AwsRoleCoverage'
import { Registrations } from '@/pages/admin/Registrations'
import { IntegrationsStatus } from '@/pages/admin/IntegrationsStatus'
import { Configuracoes } from '@/pages/admin/Configuracoes'
import { BaseDeConhecimento } from '@/pages/conhecimento'
import ConnectorsAdmin from '@/pages/admin/connectors/ConnectorsAdmin'
import SessionsList from '@/pages/admin/sessions/SessionsList'
import ZtnaAdmin from '@/pages/admin/ZtnaAdmin'
import ZtnaWizard from '@/pages/admin/ZtnaWizard'
import ZtnaCustomers from '@/pages/admin/ZtnaCustomers'
import { Register } from '@/pages/auth/Register'
import { Perfil } from '@/pages/perfil/Perfil'
import { GitlabCallback } from '@/pages/perfil/GitlabCallback'
import { Notificacoes as NotificacoesFeed, Preferencias as NotificacoesPreferencias } from '@/pages/notificacoes'
import { CsatForm } from '@/pages/csat/CsatForm'
import { DatabasePage } from '@/pages/database/DatabasePage'
import { EnderecamentoPage } from '@/pages/ipam/EnderecamentoPage'
import { SecurityOverview } from '@/pages/security/SecurityOverview'
import { TenantSecurityDetail } from '@/pages/security/TenantSecurityDetail'
import { CategoryDetail } from '@/pages/security/CategoryDetail'
import { WafRegistry } from '@/pages/security/waf/WafRegistry'
import { WafDetail } from '@/pages/security/waf/WafDetail'
import { WafLotePage } from '@/pages/security/waf/WafLotePage'
import { WafDashboardPage } from '@/pages/security/waf/WafDashboardPage'
import { CloudHsmPage } from '@/pages/security/cloudhsm/CloudHsmPage'
import { JiraTab } from '@/pages/dashboard/JiraTab'
import { JiraConfigPanel } from '@/components/jira/JiraConfigPanel'
import { WhatsApiMessages } from '@/pages/whatsapi/Messages'
import { WhatsApiConversas } from '@/pages/whatsapi/Conversas'
import { WhatsApiStats } from '@/pages/whatsapi/Stats'
import { WhatsApiApiKeys } from '@/pages/whatsapi/ApiKeys'
import { WhatsApiDocs } from '@/pages/whatsapi/Docs'
import { Frota } from '@/pages/robo/Frota'
import { RoboDetalhe } from '@/pages/robo/RoboDetalhe'
import { AjustesDoRobo } from '@/pages/robo/AjustesDoRobo'
import { MeuRobo } from '@/pages/robo/MeuRobo'
import { PedidosDeAnalise } from '@/pages/robo/PedidosDeAnalise'
import { useAuthStore, useCan } from '@/store/auth'
import { useChangelogSplash } from '@/hooks/useChangelogSplash'
import { useCriteriosDeArea } from '@/hooks/useCriteriosDeArea'
import { areasVisiveis } from '@/lib/novidades/filtro'
import { ChangelogModal } from '@/components/layout/ChangelogModal'
import AppsList from '@/pages/apps/AppsList'
import { ApplicationsLauncherTab } from '@/pages/dashboard/ApplicationsLauncherTab'
import { Alertas } from '@/pages/alertas/Alertas'
import RegrasDeOrdem from '@/pages/tenant/RegrasDeOrdem'
import AmbientePage from '@/pages/ambientes/AmbientePage'
import ChatHome from '@/pages/chat/ChatHome'
import Conversation from '@/pages/chat/Conversation'
import { AlertTeamsAdmin } from '@/pages/admin/AlertTeamsAdmin'
import { HeartbeatSourcesAdmin } from '@/pages/admin/HeartbeatSourcesAdmin'
import { SocTeamsAdmin } from '@/pages/admin/SocTeamsAdmin'
import { GatewaysWhatsApp } from '@/pages/admin/GatewaysWhatsApp'
import { MensagensSistema } from '@/pages/admin/MensagensSistema'
import { RoutineWizard } from '@/pages/rotinas/RoutineWizard'
import { RoutinesList } from '@/pages/rotinas/RoutinesList'
import { NoOrganizationGate } from '@/components/organizations/NoOrganizationScreen'
import { RequirePermission } from '@/components/auth/RequirePermission'

class ErrorBoundary extends Component<{ children: ReactNode }, { error: Error | null }> {
  constructor(props: { children: ReactNode }) {
    super(props)
    this.state = { error: null }
  }
  static getDerivedStateFromError(error: Error) {
    return { error }
  }
  componentDidCatch(error: Error, info: ErrorInfo) {
    console.error('[ErrorBoundary]', error, info)
  }
  render() {
    if (this.state.error) {
      return (
        <div style={{ padding: 32, fontFamily: 'monospace' }}>
          <h2 style={{ color: 'red' }}>Erro de renderização</h2>
          <pre style={{ background: '#f5f5f5', padding: 16, borderRadius: 4, whiteSpace: 'pre-wrap', wordBreak: 'break-all' }}>
            {this.state.error.message}
            {'\n\n'}
            {this.state.error.stack}
          </pre>
          <button onClick={() => this.setState({ error: null })} style={{ marginTop: 16 }}>
            Tentar novamente
          </button>
        </div>
      )
    }
    return this.props.children
  }
}

export function RedirectToBrokerLogin() {
  useEffect(() => {
    const ret = window.location.pathname + window.location.search
    // Evita loop: se user esta em /login (que renderiza este proprio
    // componente), usar / como return_to. Senao callback redireciona
    // pra /login, SPA monta RedirectToBrokerLogin de novo, infinito.
    const safeRet = (ret === '/login' || ret.startsWith('/login?')) ? '/' : ret
    window.location.replace(
      '/api/v1/auth/oidc/start?return_to=' + encodeURIComponent(safeRet),
    )
  }, [])
  return null
}

function RequireAuth({ children }: { children: React.ReactNode }) {
  const { isAuthenticated, isRestoring, mustChangePassword, user } = useAuthStore()
  // Mantém a sessão do broker ZTNA viva junto com a atividade no ConsoleAI
  // (gateado por feature flag; no-op se OFF/não-autenticado). Chamado no topo,
  // antes dos early-returns, pra respeitar as regras de hooks.
  useZtnaSessionHeartbeat()
  // Logout por inatividade real (mouse/teclado) — fecha a lacuna do idle-timeout
  // deslizante do servidor (gateado por feature flag; no-op se OFF/não-auth).
  // Após inatividade, abre aviso com contagem; sem cancelar → logout.
  usePwaPersistentSession()
  const { warningActive, secondsLeft, staySignedIn } = useInactivityLogout()
  // Splash de novidades: abre automaticamente quando há versões novas desde o último login.
  // Chamado no topo (antes dos early-returns) para respeitar as regras de hooks.
  // As áreas visíveis esperam os critérios nomeados carregarem (Gateways WhatsApp vem do backend).
  const can = useCan()
  const { carregado: criteriosCarregados, criterios, criteriosComErro } = useCriteriosDeArea()
  const visiveis = user && criteriosCarregados ? areasVisiveis(user, can, criterios, criteriosComErro) : null
  const splash = useChangelogSplash(user?.last_seen_changelog_version, visiveis, user?.id)
  if (isRestoring) return (
    <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'center', height: '100vh' }}>
      <div style={{ width: 32, height: 32, border: '3px solid var(--ai-border-light)', borderTopColor: '#025468', borderRadius: '50%', animation: 'spin 0.7s linear infinite' }} />
      <style>{`@keyframes spin { to { transform: rotate(360deg) } }`}</style>
    </div>
  )
  if (!isAuthenticated) return <RedirectToBrokerLogin />
  // MR-E: bloqueia acesso se setup inicial pendente
  const needsSetup = mustChangePassword
    || (user?.missing_required_fields?.length ?? 0) > 0
    || (user != null && user.has_totp === false)
  if (needsSetup) return <Navigate to="/auth/setup" replace />
  return (
    <>
      {/* Gate colaborador sem organização (spec 2026-07-24 §3.1): intercepta
          não-admins sem org com a tela de solicitação de entrada. Admins e quem
          tem org seguem o fluxo normal (children). Acoplamento mínimo: um único
          ponto pós-auth, sem tocar o AppLayout. */}
      <NoOrganizationGate>{children}</NoOrganizationGate>
      {warningActive && (
        <InactivityWarningModal secondsLeft={secondsLeft} onStay={staySignedIn} />
      )}
      {!needsSetup && splash.open && (
        <ChangelogModal
          opened
          onClose={splash.dismiss}
          novidades={{ entradas: splash.entries, versoes: splash.versoesNovas }}
        />
      )}
    </>
  )
}

function SessionRestorer({ children }: { children: React.ReactNode }) {
  const { restoreSession, isAuthenticated } = useAuthStore()
  useEffect(() => {
    if (!isAuthenticated) restoreSession()
  }, [])
  return <>{children}</>
}

export default function App() {
  return (
    <BrowserRouter>
      <ErrorBoundary>
      <SessionRestorer>
        <Routes>
          <Route path="/login" element={<RedirectToBrokerLogin />} />
          <Route path="/session-expired" element={<SessionExpired />} />
          {/* Férias (§3.3): fora do RequireAuth — o colaborador afastado não
              tem sessão válida, então a tela precisa viver antes do gate. */}
          <Route path="/ferias" element={<OnLeave />} />
          <Route path="/auth/change-password" element={<ChangePassword />} />
          <Route path="/auth/setup" element={<SetupWizard />} />
          <Route path="/auth/forgot-password" element={<ForgotPassword />} />
          <Route path="/auth/reset-password" element={<ResetPassword />} />
          <Route path="/auth/forgot-totp" element={<ForgotTotp />} />
          <Route path="/auth/setup-phone" element={<SetupPhone />} />
          <Route path="/register" element={<Register />} />
          <Route path="csat/:token" element={<CsatForm />} />
          <Route
            element={
              <RequireAuth>
                <AppLayout />
              </RequireAuth>
            }
          >
            <Route index element={<HomeLanding />} />
            <Route path="chat/robo" element={<AbrirConversaDoRobo />} />
            <Route path="dashboard/dev" element={<DevDashboard />} />
            <Route path="dashboard/po" element={<PODashboard />} />
            <Route path="dashboard/manager" element={<ManagerDashboard />} />
            <Route path="apps" element={<AppsList />} />
            <Route path="aplicativos" element={<ApplicationsLauncherTab />} />
            {/* Sem RequirePermission: quem não tem pasta vê o estado vazio, e o gate
                real é do backend (spec 2026-09-26-kb-pastas, §7). */}
            <Route path="conhecimento" element={<BaseDeConhecimento />} />
            {/* Aba/página "Atendente IA" aposentada — o @Atendente é inline no chat.
                Redireciona bookmarks antigos para o chat (Conversas → @Atendente). */}
            <Route path="assistente" element={<Navigate to="/chat" replace />} />
            <Route path="recursos"  element={<Recursos />} />
            <Route path="ssm"       element={<Ssm />} />
            <Route path="chamados"  element={<Chamados />} />
            <Route path="chamados/novo" element={<NovoChamado />} />
            <Route path="chamados/inbox" element={<ChamadosInbox />} />
            <Route path="chamados/:id" element={<ChamadoDetalhe />} />
            {/* D4 (spec 2026-09-27): a "Minha fila" saiu; o favorito antigo cai na Entrada. */}
            <Route path="minha-fila" element={<Navigate to="/chamados/inbox" replace />} />
            <Route path="chamados/relatorios" element={<Relatorios />} />
            <Route path="chamados/como-funciona" element={<ComoFunciona />} />
            <Route path="chamados/relatorio-mudancas" element={<RelatorioMudancas />} />
            <Route path="gmud" element={<RequirePermission perm="chamados:gmud_read"><Gmud /></RequirePermission>} />
            <Route path="gmud/nova" element={<RequirePermission perm="chamados:gmud_read"><GmudForm /></RequirePermission>} />
            {/* gmud/:id fica SEM RequirePermission de proposito — nao e esquecimento.
                A autoridade de leitura de UMA GMUD e o backend (`can_read_gmud`,
                gmud_service.py), que concede por cinco fontes; TRES sao relacionamento
                com aquela GMUD especifica (solicitante, nomeado em `gmud_approvals`,
                assignee) e a rota nao tem como consultar nenhuma: aqui so existe
                `can(resource, action)`, que nao sabe de qual GMUD se trata.
                Gatear por `chamados:gmud_read` contradiria a autoridade e barraria
                com <Forbidden/> justamente o aprovador nomeado sem a permissao — quem
                precisa entrar para VOTAR, porque o GmudCabPanel e a unica superficie
                de voto do produto e vive so dentro desta rota. Quem nao pode ler
                recebe 403 do GET e a propria pagina mostra a recusa.
                A lista (`gmud`) e a criacao (`gmud/nova`) seguem gateadas: aquelas
                SAO decididas por permissao, sem eixo de relacionamento. */}
            <Route path="gmud/:id" element={<GmudDetalhe />} />
            <Route path="auditoria" element={<RequirePermission perm="audit:read"><Auditoria /></RequirePermission>} />
            <Route path="auditoria/dashboard" element={<RequirePermission perm="audit:read"><AuditoriaDashboard /></RequirePermission>} />
            <Route path="auditoria/politicas" element={<RequirePermission perm="audit:read"><AuditoriaPoliticas /></RequirePermission>} />
            {/* auditoria-global é gateada por role auditor_global (não por permissão)
                — deixada sem guard de permissão para não bloquear auditores legítimos. */}
            <Route path="auditoria-global" element={<AuditoriaGlobal />} />
            <Route path="notificacoes" element={<NotificacoesFeed />} />
            <Route path="notificacoes/preferencias" element={<NotificacoesPreferencias />} />
            <Route path="alertas" element={<Alertas />} />
            {/* Regras de ordem por alerta (spec 2026-09-30 §6.2): quem decide é o GET, pelo
                tenant do CAMINHO — o seletor pode estar noutro tenant. */}
            <Route path="tenants/:tenantId/regras-de-ordem" element={<RegrasDeOrdem />} />
            {/* Ficha do ambiente criado (spec 2026-10-09 §8.1): o GET decide pelo tenant do
                CAMINHO e filtra pelo perfil — sem guard de permissão aqui. */}
            <Route path="ambientes/:tenantId/:stageKey" element={<AmbientePage />} />
            <Route path="chat" element={<ChatHome />} />
            <Route path="chat/:conversationId" element={<Conversation />} />
            <Route path="rotinas" element={<RoutinesList />} />
            <Route path="rotinas/nova" element={<RoutineWizard />} />
            {/* FinOps (M-07): defense-in-depth com finops:read (tenant-scoped, também
                honra permissão global). Redirects não precisam de guard. */}
            <Route path="finops">
              <Route index element={<RequirePermission perm="finops:read"><FinopsHome /></RequirePermission>} />
              {/* Redirects de rotas aposentadas → /finops (1 release) — TASK-2678 */}
              <Route path="dashboard" element={<Navigate to="/finops" replace />} />
              <Route path="sumario"   element={<Navigate to="/finops" replace />} />
              <Route path="recomendacoes" element={<RequirePermission perm="finops:read"><Recomendacoes /></RequirePermission>} />
              {/* Wave 2 (S4 Polish) — rotas canônicas */}
              <Route path="alertas"       element={<RequirePermission perm="finops:read"><AlertasV2 /></RequirePermission>} />
              <Route path="reports"       element={<RequirePermission perm="finops:read"><RelatoriosV2 /></RequirePermission>} />
              <Route path="reports/:id"   element={<RequirePermission perm="finops:read"><RelatorioDetalheV2 /></RequirePermission>} />
              {/* Org Tree report (FinOps Improvement) */}
              <Route path="relatorios/organizacao" element={<RequirePermission perm="finops:read"><RelatorioOrganizacao /></RequirePermission>} />
              {/* SP5 — Assinaturas de relatório agendado (gated por flag na própria tela) */}
              <Route path="relatorios/assinaturas" element={<RequirePermission perm="finops:read"><RelatorioAssinaturas /></RequirePermission>} />
              <Route path="contas/:accountId" element={<RequirePermission perm="finops:read"><ContaDetalhe /></RequirePermission>} />
              {/* Aliases backward-compat (S3 frontend) */}
              <Route path="alertas-v2"        element={<RequirePermission perm="finops:read"><AlertasV2 /></RequirePermission>} />
              <Route path="relatorios-v2"     element={<RequirePermission perm="finops:read"><RelatoriosV2 /></RequirePermission>} />
              <Route path="relatorios-v2/:id" element={<RequirePermission perm="finops:read"><RelatorioDetalheV2 /></RequirePermission>} />
            </Route>
            <Route path="database" element={<RequirePermission perm="rds:read"><DatabasePage /></RequirePermission>} />
            {/* IPAM: `ipam:view` é permissão de PLATAFORMA e tudo-ou-nada — a tela
                mostra o endereçamento de todos os tenants junto (§8.8). */}
            <Route path="enderecamento" element={<RequirePermission perm="ipam:view"><EnderecamentoPage /></RequirePermission>} />
            <Route path="security" element={<RequirePermission perm="security:read"><SecurityOverview /></RequirePermission>} />
            <Route path="security/waf" element={<RequirePermission perm="waf:view"><WafRegistry /></RequirePermission>} />
            <Route path="security/waf/lotes/:loteId" element={<RequirePermission perm="waf:view"><WafLotePage /></RequirePermission>} />
            <Route path="security/waf/:wafId" element={<RequirePermission perm="waf:view"><WafDetail /></RequirePermission>} />
            <Route path="security/waf-dashboard" element={<RequirePermission perm="waf:view"><WafDashboardPage /></RequirePermission>} />
            <Route path="security/cloudhsm" element={<RequirePermission perm="cloudhsm:view"><CloudHsmPage /></RequirePermission>} />
            <Route path="security/tenant/:tenantId" element={<RequirePermission perm="security:read"><TenantSecurityDetail /></RequirePermission>} />
            <Route path="security/category/:category" element={<RequirePermission perm="security:read"><CategoryDetail /></RequirePermission>} />
            <Route path="perfil" element={<Perfil />} />
            <Route path="perfil/gitlab/callback" element={<GitlabCallback />} />
            {/* Admin (M-07): rotas de plataforma gateadas por permissão GLOBAL
                (global={true}) — espelha o gate do backend. Rotas ambíguas
                (organizacoes p/ gestor, ztna/connectors/sessions, soc p/ gestor)
                ficam sem guard para não bloquear navegação legítima (backend
                segue autoridade). */}
            <Route path="admin">
              <Route path="usuarios"      element={<RequirePermission perm="usuarios:manage" global><Usuarios /></RequirePermission>} />
              <Route path="tenants"       element={<RequirePermission perm="admin:platform" global><Tenants /></RequirePermission>} />
              <Route path="tenants/new"   element={<RequirePermission perm="admin:platform" global><NewTenantWizard /></RequirePermission>} />
              <Route path="tenants/jobs"      element={<RequirePermission perm="admin:platform" global><ProvisioningJobsList /></RequirePermission>} />
              <Route path="tenants/jobs/:id"  element={<RequirePermission perm="admin:platform" global><ProvisioningJobDetail /></RequirePermission>} />
              {/* aws-ou-tree route removida em v1.24.3 — view migrada pra TenantsGroupedView */}
              <Route path="permissoes"    element={<RequirePermission perm="usuarios:manage" global><Permissoes /></RequirePermission>} />
              <Route path="conexoes-sso"  element={<RequirePermission perm="admin:platform" global><ConexoesSso /></RequirePermission>} />
              <Route path="roles"         element={<RequirePermission perm="admin:platform" global><Roles /></RequirePermission>} />
              <Route path="ldap"          element={<RequirePermission perm="ldap:manage" global><LdapAdmin /></RequirePermission>} />
              <Route path="chamados/categorias" element={<Categorias />} />
              <Route path="chamados/templates" element={<FormTemplates />} />
              <Route path="chamados/alertas" element={<AlertaRules />} />
              <Route path="gmud/freeze" element={<GmudFreezeAdmin />} />
              <Route path="ccm/comites"   element={<RequirePermission perm="ccm:manage" global><ComitesAdmin /></RequirePermission>} />
              {/* Gate GLOBAL: `certificacoes` e recurso de plataforma, decidido com
                  tenant_id=None na API. `certificacoes:read` concedido dentro de um tenant
                  nao abre a tela, e e' assim de proposito. */}
              <Route path="certificacoes" element={<RequirePermission perm="certificacoes:read" global><CertificacoesAdmin /></RequirePermission>} />
              <Route path="organizacoes" element={<Organizacoes />} />
              <Route path="registrations"      element={<RequirePermission perm="usuarios:manage" anyRole={['gestor', 'admin']} global><Registrations /></RequirePermission>} />
              <Route path="registrations/:id"  element={<RequirePermission perm="usuarios:manage" anyRole={['gestor', 'admin']} global><Registrations /></RequirePermission>} />
              <Route path="integracoes" element={<RequirePermission perm="usuarios:manage" global><IntegrationsStatus /></RequirePermission>} />
              {/* /admin/apps removida (2026-05-10) — fluxo legacy redirecionado pro dashboard ZTNA. */}
              <Route path="apps" element={<Navigate to="/admin/ztna" replace />} />
              <Route path="connectors" element={<ConnectorsAdmin />} />
              <Route path="sessions" element={<SessionsList />} />
              <Route path="ztna" element={<ZtnaAdmin />} />
              <Route path="ztna/onboard" element={<ZtnaWizard />} />
              <Route path="ztna/customers" element={<ZtnaCustomers />} />
              <Route path="aws-access" element={<RequirePermission perm="admin:platform" global><AwsRoleCoverage /></RequirePermission>} />
              <Route path="configuracoes" element={<RequirePermission perm="admin:platform" global><Configuracoes /></RequirePermission>} />
              <Route path="conhecimento" element={<Navigate to="/conhecimento" replace />} />
              <Route path="alert-teams" element={<RequirePermission perm="alerts:manage" global><AlertTeamsAdmin /></RequirePermission>} />
              <Route path="heartbeat-sources" element={<RequirePermission perm="alerts:manage" global><HeartbeatSourcesAdmin /></RequirePermission>} />
              {/* soc: gate OU (soc:manage de plataforma, derivada de alerts:manage
                  via MIGRATION_243, nunca aparece como linha própria em
                  global_permissions — RequirePermission não a acharia — OU
                  gestor de organização). SocTeamsAdmin trata o acesso por
                  dentro, no padrão de Organizacoes; o backend segue autoridade
                  (403 nas rotas via pode_administrar_soc). */}
              <Route path="soc" element={<SocTeamsAdmin />} />
              {/* Gateways WhatsApp: permissão POR ORGANIZAÇÃO (organization_users.template_id),
                  que o RequirePermission não expressa (ele olha permissão global ou do tenant
                  selecionado). A página decide pelo GET /v1/whatsapp-gateways/organizacoes e o
                  backend segue autoridade (403 por organização). Padrão de soc/organizacoes. */}
              <Route path="whatsapp-gateways" element={<GatewaysWhatsApp />} />
              {/* Mensagens do sistema: gate é OR (notificacoes:template_write OU
                  admin:platform), que o RequirePermission não expressa — a própria
                  tela recusa quem não tem nenhuma das duas. */}
              <Route path="notificacoes" element={<MensagensSistema />} />
            </Route>
            <Route path="integracoes">
              <Route path="jira" element={<JiraTab />} />
              <Route path="jira/config" element={<JiraConfigPanel />} />
            </Route>
            <Route path="whatsapi">
              <Route path="messages" element={<WhatsApiMessages />} />
              <Route path="conversas" element={<WhatsApiConversas />} />
              <Route path="stats" element={<WhatsApiStats />} />
              <Route path="api-keys" element={<WhatsApiApiKeys />} />
              <Route path="docs" element={<WhatsApiDocs />} />
            </Route>
            {/* Robô. Caminhos planos de proposito: a guarda de menu
                (components/layout/__tests__/roboMenu.test.ts) le `path="robo/..."`
                no fonte, e rota aninhada escaparia dela. */}
            <Route path="robo/frota" element={<RequirePermission perm="robo:read" global><Frota /></RequirePermission>} />
            <Route path="robo/frota/:publicId" element={<RequirePermission perm="robo:read" global><RoboDetalhe /></RequirePermission>} />
            {/* Mesma porta das rotas de ajustes no backend: `robo:configure` GLOBAL. */}
            <Route path="robo/frota/:publicId/ajustes" element={<RequirePermission perm="robo:configure" global><AjustesDoRobo /></RequirePermission>} />
            {/* Sem RequirePermission de proposito: a porta e' POSSE, nao
                permissao — quem recebe um robo tem de poder ajusta-lo sem
                depender de quem o atribuiu. Sem robo, a tela mostra o estado
                vazio; a autoridade e' o 404 do backend. */}
            <Route path="robo/meu" element={<MeuRobo />} />
            {/* Pedidos de análise (TASK-12127): sem permissão de rota — a lista já vem
                filtrada pelo servidor, e quem não pode pegar vê a lista vazia. */}
            <Route path="robo/pedidos" element={<PedidosDeAnalise />} />
            {/* Notificacoes e Aparencia viraram secoes de Meu robo (spec
                2026-09-27, 4.5); o link antigo cai na secao, nao num 404. */}
            <Route path="robo/meu/notificacoes" element={<Navigate to="/robo/meu#alertas" replace />} />
            <Route path="robo/meu/aparencia" element={<Navigate to="/robo/meu#aparencia" replace />} />
          </Route>
          <Route path="*" element={<Navigate to="/" replace />} />
        </Routes>
      </SessionRestorer>
      </ErrorBoundary>
    </BrowserRouter>
  )
}
